import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import * as Hangul from 'hangul-js';

import WithSideBarAndNavBar from '../../layouts/withSideBarAndNavBar/withSideBarAndNavBar';
import './ListOfEmployees.scss';
import SearchBox from './searchBox/SearchBox';
import SelectionBox from './selectionBox/SelectionBox';
import useLoadingStore from 'zustands/useLoadingStore';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';
import { IEmpInfoData } from 'types/listOfEmployees';
import moment from 'moment';
import { phoneNumberAddHypen } from 'constants/utils';
import { IOption } from 'types/common';
import { positionTypeOptions, payTypeOptions, calendarTypeOptions } from 'constants/dataArr';
import { SingleValue } from 'react-select';
import useUserStore from 'zustands/useUserStore';
import ListOfNotWorkingEmployeesTable from './ListOfNotWorkingEmployeesTable';
import ListOfEmployeesTooltip from './ListOfEmployeesTooltip';

const ListOfEmployees = () => {
  const navigate = useNavigate();

  const { setLoadingState } = useLoadingStore();
  const { userType, managerInfo } = useUserStore();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [empListData, setEmpListData] = React.useState<IEmpInfoData[]>([]);
  const [selectIds, setSelectIds] = React.useState<number[]>([]);
  const [selectedOptions, setSelectedOptions] = React.useState({
    position: { value: '전체', label: '전체' },
    payType: { value: '전체', label: '전체' },
    calendar: { value: '전체', label: '전체' },
  });
  const [searchValue, setSearchValue] = React.useState<string>('');

  // 직원 기본, 급여정보 데이터 패칭
  const fetchEmpData = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/getEmps`);
      if (data.resultCode === 0) {
        const allEmp = [...data.resultData.emps, ...data.resultData.empsEnd];
        const emp = allEmp.map((i: IEmpInfoData) => ({
          MEMBER_SEQ: i.MEMBER_SEQ,
          MEM_MEMBER_SEQ: i.MEM_MEMBER_SEQ,
          EMP_SEQ: i.EMP_SEQ,
          EMP_NAME: i.EMP_NAME,
          IS_MANAGER: i.IS_MANAGER.toString(),
          MobileNo: i.MobileNo,
          BIRTHDATE: i.BIRTHDATE,
          CALENDAR: i.CALENDAR, // '1': 자율 '0': 일정
          PAY_TYPE: i.PAY_TYPE, // '0': 시급 '1': 일급 '2': 월급
          PAY: i.PAY,
          MEALS: i.MEALS,
          SELF_DRIVING: i.SELF_DRIVING,
          BONUS: i.BONUS, // 상여
          INCENTIVE: i.INCENTIVE, // 성과급
          START: i.START,
          END: i.END,
          PAY_START: i.PAY_START,
          PAY_SHOW: i.PAY_SHOW.toString(), // 본인급여 확인 권한
          STOREPAY_SHOW: i.STOREPAY_SHOW, // 사업장급여 확인 권한
          OTHERPAY_SHOW: i.OTHERPAY_SHOW.toString(), // 타직원 정보 및 급여 확인 권한
          CalendarEdit: i.CalendarEdit.toString(), // 출퇴근정보 수정 권한
          PUSH: i.PUSH, // 타직원 알림 받기
          WEEKEND_ALLOWANCE: i.WEEKEND_ALLOWANCE.toString(), // 주휴수당 자동가산 사용여부
          WeekTime: i.WeekTime,
          WeekType: i.WeekType, // 0: 근로기준법(자동) 1: 월근무시간(수동)
          REST_ALLOWANCE: i.REST_ALLOWANCE.toString(), // 휴게시간 자동차감 사용여부
          RestTime: i.RestTime,
          RestType: i.RestType, // 0: 근로기준법(자동) 1: 월근무시간(수동)
          THREE_ALLOWANCE: i.THREE_ALLOWANCE.toString(), // 3대 수당 사용여부
          insurance: i.insurance, // '2': 4대보험 '1': 3.3% '0': 없음
          pension: i.pension,
          health: i.health,
          employment: i.employment,
          accident: i.accident,
          probation: i.probation, // 수습사용 여부
          probationDATE: i.probationDATE,
          probationPercent: i.probationPercent,
          Annual_START: i.Annual_START,
          Annual: Number(i.Annual),
          USE_Annual: Number(i.USE_Annual),
          RemainderAnnual: Number(i.RemainderAnnual),
          SCHEDULE_INFO: i.SCHEDULE_INFO,
        }));
        // 매니저고 타직원 정보수정 권한 없는경우 본인 데이터만 표시
        if (userType === '0' && managerInfo.otherPayShow === false) {
          const onlyManagerData = emp.filter((i) => i.EMP_SEQ === managerInfo.empSeq);
          setEmpListData(onlyManagerData);
        } else {
          setEmpListData(emp);
        }
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/getEmps');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 필터링 셀렉터 상태 데이터 핸들링
  const handleFilterSelect = (value: SingleValue<IOption>, name: string) => {
    setSelectedOptions((prev) => ({ ...prev, [name]: value }));
  };

  // 검색어 인풋 입력 상태 핸들
  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  // 상세정보 화면 이동
  const enterDetailInfo = (empData: IEmpInfoData) => {
    navigate('/view-information', {
      state: {
        empData,
      },
    });
  };

  const tableHeaders = ['상태', '근무일정', '직책', '급여유형', '근무유형', '휴대폰번호', '더보기'];
  const customCalendarOptions = [{ value: '전체', label: '전체' }, ...calendarTypeOptions];
  const customPositionOptions = [{ value: '전체', label: '전체' }, ...positionTypeOptions];
  const customPayTypeOptions = [{ value: '전체', label: '전체' }, ...payTypeOptions];
  const filteredEmpListData = empListData
    .filter((i) => {
      return i.END === null || (i.END !== null && moment(i.END).isAfter(moment()));
    })
    .filter((i) => {
      if (selectedOptions.position.label !== '전체') {
        return selectedOptions.position.value === i.IS_MANAGER;
      }
      return i;
    })
    .filter((i) => {
      if (selectedOptions.payType.label !== '전체') {
        return selectedOptions.payType.value === i.PAY_TYPE;
      }
      return i;
    })
    .filter((i) => {
      if (selectedOptions.calendar.label !== '전체') {
        return selectedOptions.calendar.value === i.CALENDAR;
      }
      return i;
    });

  // 데이터 검색어 필터링
  const searchFilteredEmpListData = () => {
    const list = filteredEmpListData;
    if (list.length > 0) {
      const search1 = searchValue.replace(/-/g, '');
      const search2 = Hangul.disassemble(search1).join('');
      const result = list.filter((item) => {
        return (
          item.EMP_NAME?.includes(search1) || item.MobileNo?.includes(search1) || item.disassembled?.includes(search2)
        );
      });

      return searchValue === '' ? filteredEmpListData : result;
    }
    return [];
  };

  React.useEffect(() => {
    fetchEmpData();
  }, []);

  return (
    <div className="listOfEmployeesScreen">
      <WithSideBarAndNavBar>
        <div className="header">
          <div className="leftContainer">근무직원목록</div>
          {/* <div className="rightContainer">
            <div className="textLabel">한번에 직원정보를 변경하고 싶다면?</div>
            <button
              className="button"
              onClick={() => {
                navigate('/batch-application');
              }}>
              근로템플릿 일괄적용
            </button>
            <button
              className="button"
              onClick={() => {
                navigate('/view-information');
              }}>
              일정템플릿 일괄적용
            </button>
          </div> */}
        </div>

        <div className="detailCard">
          <div className="cardHeader">
            <div className="leftContainer">
              총{' '}
              {
                empListData.filter((i) => {
                  return i.END === null || (i.END !== null && moment(i.END).isAfter(moment()));
                }).length
              }
              명
            </div>
            <div className="rightContainer">
              <SelectionBox
                label="직책"
                name="position"
                value={selectedOptions.position}
                options={customPositionOptions}
                onChange={handleFilterSelect}
              />
              <SelectionBox
                label="급여유형"
                name="payType"
                value={selectedOptions.payType}
                options={customPayTypeOptions}
                onChange={handleFilterSelect}
              />
              <SelectionBox
                label="근무유형"
                name="calendar"
                value={selectedOptions.calendar}
                options={customCalendarOptions}
                onChange={handleFilterSelect}
              />
              <SearchBox
                value={searchValue}
                name={'searchValue'}
                onChange={handleSearchInput}
                placeHolder="이름 또는 휴대폰번호 검색"
                maxLength={20}
              />
            </div>
          </div>

          <div className="tableContainer">
            <table>
              <tr>
                <th>
                  <div className="flexibleColumns">이름</div>
                </th>

                {tableHeaders.map((header) => (
                  <th key={header}>{header}</th>
                ))}
              </tr>

              {empListData.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: '#ccc' }}>
                    합류된 직원이 없습니다
                  </td>
                </tr>
              ) : searchFilteredEmpListData().length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: '#ccc' }}>
                    해당 직원이 없습니다
                  </td>
                </tr>
              ) : (
                searchFilteredEmpListData().map((i) => {
                  const isWorking = i.END === null || (i.END !== null && moment(i.END).isAfter(moment()));
                  return (
                    <tr key={i.EMP_SEQ}>
                      <td>
                        <div className="flexibleColumns">
                          <div className="nameCircle">{i.EMP_NAME.substring(1).substr(0, 2)}</div>
                          <div className="nameText">{i.EMP_NAME}</div>
                        </div>
                      </td>
                      <td>
                        <div className="stateCellContainer">
                          <div
                            className="stateIndicator"
                            style={{
                              backgroundColor: isWorking ? '#E47C77' : ' #49AE9E',
                            }}
                          />

                          {isWorking ? '재직' : '퇴직'}
                          {i.probation === '1' ? (
                            moment(i.probationDATE).isBefore(moment()) ? (
                              <div className="probation">수습완료</div>
                            ) : (
                              <div className="probation">수습중</div>
                            )
                          ) : null}
                        </div>
                      </td>
                      <td>
                        {i?.SCHEDULE_INFO?.length === 0 ? (
                          <div>일정 없음</div>
                        ) : (
                          <ListOfEmployeesTooltip text={i.SCHEDULE_INFO}>
                            <div>일정 있음</div>
                          </ListOfEmployeesTooltip>
                        )}
                      </td>
                      <td>{i.IS_MANAGER === '1' ? '매니저' : '스태프'}</td>
                      <td>{i.PAY_TYPE === '0' ? '시급' : i.PAY_TYPE === '1' ? '일급' : '월급'}</td>
                      <td>
                        <div className={`typeContainer ${i.CALENDAR === '1' ? ' redType' : ' blueType'}`}>
                          {i.CALENDAR === '0' ? '고정근무' : '자율출퇴근'}
                        </div>
                      </td>
                      <td>{phoneNumberAddHypen(i.MobileNo)}</td>
                      <td>
                        {i.MEM_MEMBER_SEQ ? (
                          <button className="actionButton" onClick={() => enterDetailInfo(i)}>
                            정보수정
                          </button>
                        ) : (
                          <button className="disabledButton" disabled>
                            탈퇴직원
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </table>
          </div>
        </div>
        <ListOfNotWorkingEmployeesTable empListData={empListData} fetchEmpData={fetchEmpData} />
      </WithSideBarAndNavBar>
    </div>
  );
};

export default ListOfEmployees;
